import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { getDateRangeWithSessionFallback } from '#component/general/date-range/helper';
import {
  FETCH_DEVICE_LOGS,
  FETCH_DEVICE_LOGS_STARTED,
  FETCH_DEVICE_LOGS_FAILED,
  DEVICE_LOGS_PAGINATED_CHANGE_PAGE,
  DEVICE_LOGS_CHANGE_ORDER,
  DEVICE_LOGS_CLEAR
} from '#action/types';
import { getRequestOptions, getDateRange, getURLParameter, setIdsParam } from '#helper';
import { deviceEventType } from '#type';

export const deviceLogEventTypeSet = new Set(Object.values(deviceEventType));

let abortController = new AbortController();

export const fetchDeviceLogs = () => async (dispatch, getState) => {
  try {
    abortController.abort();
    dispatch({
      type: FETCH_DEVICE_LOGS_STARTED
    });

    const filter = getState().listFilter;
    const sourcePath = 'device-log';
    const logFilter = filter[sourcePath];
    const { page, itemsPerPage, order } = getState().deviceLogs;
    const urlPage = getURLParameter('page');
    const offset = (Number(urlPage || page) - 1) * itemsPerPage;
    const searchParams = new URLSearchParams({
      order,
      limit: 50,
      offset
    });

    const projectValue = logFilter?.project?.value;
    const deviceValue = logFilter?.device?.value;
    const userValue = logFilter?.user?.value;
    const eventValue = logFilter?.event?.value;
    const { start: dateRangeStart, end: dateRangeEnd } = getDateRangeWithSessionFallback(
      logFilter?.start?.value,
      logFilter?.end?.value
    );

    setIdsParam(searchParams, 'project', projectValue);
    setIdsParam(searchParams, 'device', deviceValue);
    setIdsParam(searchParams, 'user', userValue);

    if (eventValue?.length) {
      setIdsParam(searchParams, 'event', eventValue);
    } else {
      searchParams.set('event', [...deviceLogEventTypeSet]);
    }

    if (dateRangeStart && dateRangeEnd) {
      const range = getDateRange(dateRangeStart, dateRangeEnd);
      searchParams.set('start', range.start);
      searchParams.set('end', range.end);
    }

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });
    const url = webPortalApi.buildApiUrl(`log?${searchParams.toString()}`);
    const res = await fetch(url, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      const response = await res.json();
      dispatch({
        type: FETCH_DEVICE_LOGS_FAILED,
        error: response
      });
      return dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );
    }

    const { logs = [], last } = await res.json();

    return dispatch({
      type: FETCH_DEVICE_LOGS,
      totalCount: last,
      payload: logs
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return false;
    }
    return dispatch({
      type: FETCH_DEVICE_LOGS_FAILED,
      error: e
    });
  }
};

export const changeOrder = (newOrder, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: DEVICE_LOGS_CHANGE_ORDER,
    payload: newOrder
  });

  if (refetch) {
    dispatch(fetchDeviceLogs());
  }
};

export const changePage = (newPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: DEVICE_LOGS_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchDeviceLogs());
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: DEVICE_LOGS_CLEAR
  });
};
